import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import momentTz from 'moment-timezone';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { BigCalendar } from 'lib/ui';
import Content from 'views/components/ui/content';
import { customEventPropGetter, localizer } from 'utils/builders/calendar';
import { GET_CLINIC_DAY_GRID } from 'api/graphql/v2/queries/Ops';
import { IClinicDayColumn, IOpsClinic } from 'model/ops';
import {
  applyColumnOrder,
  buildDayEvents,
  isShort,
  moveColumn,
  NO_CLINICIANS,
  orderColumns,
  parseIdList,
  resolveSelection,
  selectionFromFilters,
  toResources
} from 'utils/mappers/response/clinicDayGrid';
import { WEEKDAY_LABELS } from 'utils/mappers/response/clinicDtClinicians';
import CalendarEvent from 'views/components/calendar/mainCalendar/event';
import { CALENDAR_MODES } from 'views/components/staff/profile/tabs/calendarModes';
import { ClinicianPicker } from './ClinicianPicker';
import { OpsHeader } from './OpsHeader';
import { formatProviderName } from 'utils/format';
import { CrumbsWrapper, DayGridWrapper } from './style';

/** Monday is 1 in the weekly shape, matching Date#getDay. */
const WEEKDAY_SLUGS = ['mon', 'tue', 'wed', 'thu', 'fri'];
const weekdayFromSlug = (slug?: string) => {
  const index = WEEKDAY_SLUGS.indexOf((slug || '').toLowerCase());
  return index === -1 ? 1 : index + 1;
};

/** The grid draws one weekday, so the clock runs over that day's date. */
const dateForWeekday = (weekday: number) =>
  momentTz()
    .startOf('week')
    .add(weekday, 'days')
    .toDate();

/** The weekday switcher owns navigation; the calendar changes nothing itself. */
const noop = () => undefined;

/** Matches the week view: the label of a template slot, on hover. */
const availabilityTooltip = (event: any) =>
  event?.type === 'availability' ? event.title : undefined;

/**
 * The same calendar the DT week view draws, over one weekday.
 *
 * Every axis that decides how a slot reads comes from here rather than being
 * restated locally — the clock included. It used to run 7am to 7pm while the
 * week view ran midnight to midnight scrolled to 8am, so the same template
 * showed a different span depending on which view you opened it in.
 */
const DAY_GRID_MODE = CALENDAR_MODES.dayGrid;

const Pill: React.FC<{ label: string; booked: number; target: number }> = ({
  label,
  booked,
  target
}) => (
  <span className={`pill ${isShort(booked, target) ? 'short' : 'met'}`}>
    {label} {booked}/{target}
  </span>
);

export const ClinicDayGrid: React.FC<{
  clinicId: string;
  clinicName: string;
  /** The zone Starts and Ending dates are read in, as on the week view. */
  clinicTimezone?: string;
  weekday: number;
  columns: IClinicDayColumn[];
  onWeekdayChange: (weekday: number) => void;
  /** Null means everyone; an explicit list once one has been removed. */
  selectedIds: number[] | null;
  setSelectedIds: (next: number[] | null) => void;
  /**
   * How the drill-down narrows this grid: the discipline or grade whose figure
   * was clicked, rather than the ids behind it. Only consulted while nobody has
   * touched the picker — an explicit selection is the later, deliberate word.
   */
  disciplineFilter?: string | null;
  specialtyFilter?: string | null;
  /** Null while the default discipline order stands. */
  order: number[] | null;
  setOrder: (next: number[] | null) => void;
  /**
   * Whether anything has changed since the view opened. Decided by the page,
   * which is what remembers the arrival query — a clinicians= param alone
   * cannot tell a link the reader followed from a choice they made here.
   */
  canReset: boolean;
  /**
   * Back to the arrival query. One callback rather than two setters, because
   * both write the same query string and calling them in sequence makes the
   * second undo the first.
   */
  onReset: () => void;
}> = ({
  clinicId,
  clinicName,
  clinicTimezone,
  weekday,
  columns,
  onWeekdayChange,
  selectedIds,
  setSelectedIds,
  disciplineFilter,
  specialtyFilter,
  order,
  setOrder,
  canReset,
  onReset
}) => {

  const allIds = useMemo(
    () => orderColumns(columns).map(column => column.providerId),
    [columns]
  );
  // A link can name clinicians who have moved clinic or left, or carry an id
  // edited to another clinic's. Resolving against who is actually here is what
  // stops that drawing an empty grid; an explicit "none" still means none.
  //
  // The ids win when present: they are written by the picker, so they are the
  // more recent instruction than the discipline this grid was opened from.
  const fromFilters = useMemo(
    () =>
      selectionFromFilters(
        columns,
        disciplineFilter || null,
        specialtyFilter || null
      ),
    [columns, disciplineFilter, specialtyFilter]
  );
  const resolved = useMemo(
    () => resolveSelection(selectedIds, allIds) ?? fromFilters,
    [selectedIds, allIds, fromFilters]
  );
  const selected = resolved ?? allIds;

  const loaded = useMemo(
    () => columns.filter(column => selected.includes(column.providerId)),
    [columns, selected]
  );

  const ordered = useMemo(() => applyColumnOrder(loaded, order), [
    loaded,
    order
  ]);
  const resources = useMemo(() => toResources(ordered), [ordered]);
  const events = useMemo(
    () => buildDayEvents(loaded, weekday, clinicTimezone),
    [loaded, weekday, clinicTimezone]
  );

  const columnsById = useMemo(
    () => new Map(ordered.map(column => [column.providerId, column])),
    [ordered]
  );

  // Pointer-driven rather than HTML5 drag-and-drop or react-beautiful-dnd.
  // rbc owns the header row and exposes only each cell's contents, so there is
  // nowhere to mount an rbd Droppable spanning the columns; and native HTML5
  // dragging cannot be exercised by synthetic events, which would leave the
  // gesture untestable. Plain pointer events work for a real mouse and can be
  // driven in a test.
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);

  useEffect(() => {
    if (draggingId === null) return undefined;

    /** The column header under the pointer, if any. */
    const columnUnder = (clientX: number, clientY: number) => {
      const element = document
        .elementFromPoint(clientX, clientY)
        ?.closest('[data-provider-id]');
      const id = element?.getAttribute('data-provider-id');
      return id ? Number(id) : null;
    };

    const onMove = (event: MouseEvent) => {
      event.preventDefault();
      setDropTargetId(columnUnder(event.clientX, event.clientY));
    };

    const onUp = (event: MouseEvent) => {
      const target = columnUnder(event.clientX, event.clientY);
      const current = ordered.map(column => column.providerId);
      const from = current.indexOf(draggingId);
      const to = target === null ? -1 : current.indexOf(target);
      setDraggingId(null);
      setDropTargetId(null);
      // Released off the columns, or back where it started: leave it be.
      if (from === -1 || to === -1 || from === to) return;
      setOrder(moveColumn(current, from, to));
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [draggingId, ordered, setOrder]);

  const resourceHeader = useCallback(
    ({ resource }: any) => {
      const column = columnsById.get(resource?.id);
      if (!column) return <span>{resource?.title}</span>;
      const isDragging = draggingId === column.providerId;
      const isDropTarget =
        dropTargetId === column.providerId && draggingId !== column.providerId;
      return (
        <span
          className={`clinician-column-head${isDragging ? ' dragging' : ''}${
            isDropTarget ? ' drop-target' : ''
          }`}
          data-provider-id={column.providerId}
          onMouseDown={event => {
            // Let the clinician's name still be a link.
            if ((event.target as HTMLElement).closest('a')) return;
            event.preventDefault();
            setDraggingId(column.providerId);
          }}
          title="Drag to move this clinician's column"
        >
          <span className="col-grip" aria-hidden="true">
            ⠿
          </span>
          <a
            className="clinician-name"
            href={`/providers/${column.providerId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {/* The Display Name, as on the Providers tab. The grade follows on
                its own line rather than in the name, because this column is
                narrow enough to ellipsise. */}
            {formatProviderName(column)}
          </a>
          <span className="grade">{column.specialtyAbbreviation}</span>
          <span className="pills">
            <Pill
              label="Day"
              booked={column.dayBooked}
              target={column.dayTarget}
            />
            <Pill
              label="Wk"
              booked={column.weekBooked}
              target={column.weekTarget}
            />
          </span>
          {/* Nobody is hidden for having no capacity, so an empty column says
              which kind of empty it is. */}
          {column.hasNoTemplate && (
            <span className="column-warning">No care sessions on template</span>
          )}
          {column.hasNoBookedData && (
            <span className="column-warning">
              Booked not calculated — may overstate
            </span>
          )}
        </span>
      );
    },
    [columnsById, draggingId, dropTargetId]
  );

  /**
   * The week view's own slot renderer, not a copy of it.
   *
   * A scheduler should only have to learn the template once, so a slot reads
   * identically here and on a clinician's profile — including the states this
   * grid used to omit: Pending, the ending date of an outgoing series, and each
   * non-bookable block under its own name. Anything added to the week view's
   * slot from now on arrives here with it.
   */
  const slotContent = useCallback(
    ({ event }: any) => (
      <CalendarEvent
        event={event}
        view="day"
        provider
        redesign={DAY_GRID_MODE.isTemplate}
      />
    ),
    []
  );

  /**
   * The week view's classes and colours, plus this grid's own slot state.
   *
   * Delegating rather than hand-writing a class list is what makes a pending
   * slot read as pending here: `customEventPropGetter` is where the greyed,
   * dotted treatment and the per-block-type label colours are decided, and it
   * decided them for the week view long before this grid existed.
   */
  const eventPropGetter = useCallback((event: any) => {
    const base = customEventPropGetter(event) as {
      className?: string;
      style?: object;
    };
    const durationMin =
      (new Date(event.end).getTime() - new Date(event.start).getTime()) / 60000;
    const compact =
      DAY_GRID_MODE.compactsShortAppointments &&
      durationMin <= 30 &&
      event.type !== 'na' &&
      event.type !== 'availability';
    return {
      ...base,
      className: `${base.className || ''} slot-${event.slotState}${
        compact ? ' appt-compact' : ''
      }`
    };
  }, []);

  return (
    <>
      <OpsHeader />

      <CrumbsWrapper>
        <nav aria-label="Breadcrumb">
          <Link to="/ops">Clinics</Link>
          <span className="divider" aria-hidden="true">
            /
          </span>
          <Link to={`/ops/${clinicId}`}>{clinicName}</Link>
          <span className="divider" aria-hidden="true">
            /
          </span>
          <span aria-current="page">{WEEKDAY_LABELS[weekday - 1]}</span>
        </nav>
      </CrumbsWrapper>

      <DayGridWrapper>
        {/* The same oval-day idiom the availability form uses for "Apply to
            days" (lib/ui OvalCheckboxGroup + WEEK_DAYS), so picking a weekday
            looks the same wherever it happens. One at a time here, because the
            grid draws a single day. */}
        <div className="day-switch" role="tablist" aria-label="Weekday">
          {WEEKDAY_LABELS.map((label, index) => (
            <button
              key={label}
              type="button"
              role="tab"
              className="day-oval"
              aria-selected={weekday === index + 1}
              aria-label={label}
              title={label}
              onClick={() => onWeekdayChange(index + 1)}
            >
              {/* Single letter, as WEEK_DAYS does; position disambiguates the
                  two Ts and the title carries the full name. */}
              {label.charAt(0)}
            </button>
          ))}
        </div>

        <ClinicianPicker
          columns={columns}
          selected={selected}
          canReset={canReset}
          onChange={setSelectedIds}
          onReset={onReset}
        />

        {columns.length === 0 ? (
          <p className="empty-state">No DT clinicians at {clinicName}.</p>
        ) : ordered.length === 0 ? (
          // An empty selection is a choice the user made, not an absence of
          // staff, and reads differently from a clinic with nobody in it.
          <p className="empty-state">
            No clinicians selected. Choose one to see their day.
          </p>
        ) : (
          <div className="grid-card">
            <BigCalendar
              view="day"
              views={DAY_GRID_MODE.views}
              date={dateForWeekday(weekday)}
              // The view and date are driven by the weekday switcher, not by
              // the calendar. react-big-calendar warns about a controlled prop
              // with no handler, so these say plainly that it owns neither.
              onView={noop}
              onNavigate={noop}
              events={events}
              resources={resources}
              resourceIdAccessor="id"
              resourceTitleAccessor="title"
              localizer={localizer}
              // A Lunch or Notes bar too short for its own label has a hover
              // tooltip on the week view; the shared renderer returns early for
              // availability and never draws one itself.
              tooltipAccessor={availabilityTooltip}
              // Two bars at the same time must not be drawn on top of each
              // other: the shared stylesheet pins every bar to the full column
              // width, so one would vanish. Reachable now that a pending
              // proposal can sit over a confirmed series.
              slotEventOverlap={false}
              toolbar={false}
              min={DAY_GRID_MODE.minTime}
              max={DAY_GRID_MODE.maxTime}
              scrollToTime={DAY_GRID_MODE.scrollToTime}
              step={DAY_GRID_MODE.step}
              timeslots={DAY_GRID_MODE.timeslots}
              components={{
                resourceHeader,
                event: slotContent
              }}
              eventPropGetter={eventPropGetter}
              style={{ height: 640 }}
              className="clinic-day-grid"
            />
          </div>
        )}
        <p className="grid-footnote">
          Slots come from each clinician&apos;s standing template. A series that
          has not started yet still holds its slot and is never shown as open.
        </p>
      </DayGridWrapper>
    </>
  );
};

const ClinicDayPage: React.FC<{}> = () => {
  const { clinicId, weekday: weekdaySlug } = useParams<{
    clinicId: string;
    weekday: string;
  }>();
  const id = Number(clinicId);
  const history = useHistory();
  const { search } = useLocation();
  const weekday = weekdayFromSlug(weekdaySlug);

  /**
   * AXON-3589: all three pieces of the view live in the URL.
   *
   * The weekday was already a path segment, but the clinician selection and the
   * column arrangement were component state, so a link someone sent opened the
   * right day showing the wrong clinicians in the wrong order — which is to say
   * it did not show what the sender was looking at.
   *
   * They are read from the query string rather than mirrored into state, so
   * there is one answer to "what is on screen" instead of two that can
   * disagree. Absent params mean nobody has narrowed or rearranged anything,
   * and the defaults stand.
   */
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const selectedIds = useMemo(() => parseIdList(params.get('clinicians')), [
    params
  ]);
  /**
   * How the drill-down arrives: by what the figure meant, not by the ids behind
   * it. Read here and resolved against the columns once they load.
   */
  const disciplineFilter = params.get('discipline');
  const specialtyFilter = params.get('specialty');
  const order = useMemo(() => parseIdList(params.get('order')), [params]);

  /**
   * The query this grid opened with — what Reset returns to, and what decides
   * whether Reset is worth offering at all.
   *
   * The URL alone cannot answer that: a clinician's own weekday figure links
   * with `clinicians=14`, and so does unticking everyone else in the picker.
   * Reading the param as "the reader narrowed something" therefore offered
   * Reset the moment a one-provider link was opened, on a view where nothing
   * had been done yet and Reset could only take something away.
   *
   * So what the view arrived with is remembered instead, and Reset means "back
   * to how this opened" whatever that was — a discipline, a grade, one
   * clinician, or the whole clinic. Kept per clinic: switching weekday is a
   * filter on this screen rather than a new arrival, so the baseline survives
   * it, and a shared link's own order is part of the baseline too.
   */
  const arrival = useRef<{ clinicId: string; search: string } | null>(null);
  if (!arrival.current || arrival.current.clinicId !== clinicId) {
    arrival.current = { clinicId, search: search || '' };
  }

  /**
   * Query strings compared by content, so key order cannot fake a change.
   *
   * forEach rather than Array.from(...entries()): how URLSearchParams' iterator
   * is typed differs between DOM lib versions, and over the older one the
   * entries come back as unknown[] — which the project's tsc accepted and an
   * editor on a different TypeScript did not. forEach is declared the same way
   * everywhere.
   */
  const normalise = (query: string) => {
    const pairs: string[] = [];
    new URLSearchParams(query).forEach((value, key) => {
      pairs.push(`${key}=${value}`);
    });
    return pairs.sort().join('&');
  };

  const changedSinceArrival =
    normalise(search || '') !== normalise(arrival.current.search);

  /**
   * Push, not replace. Narrowing to one clinician and dragging a column are
   * things a person does deliberately and expects to be able to undo with the
   * back button; replacing left the address bar as the only record of a state
   * you could no longer return to.
   */
  const writeParams = useCallback(
    (changes: { clinicians?: number[] | null; order?: number[] | null }) => {
      const next = new URLSearchParams(search);
      // Both keys are written in one call rather than through two setters.
      // Two calls in one handler each close over the same `search`, so the
      // second would rebuild from a stale query string and undo the first —
      // which had Reset restoring the filter it was meant to clear.
      if ('clinicians' in changes) {
        const ids = changes.clinicians;
        // The key is optional, so `in` narrows its presence but not its value:
        // it is still number[] | null | undefined here, and calling .length on
        // undefined below is a TS2532 that fails the Netlify build.
        //
        // The discipline or grade the grid was opened on is NEVER removed from
        // here. It is where the reader came from; the ids are what they did
        // since. Both live in the URL at once and the read side gives the ids
        // precedence while they are present (see `resolved`), so there is no
        // ambiguity — and Reset, which deletes the ids, lands back on the
        // discipline rather than on the whole clinic.
        //
        // Deleting the filter when ids were written is what broke that: by the
        // time Reset ran, the only record of where the reader came from had
        // already been thrown away.
        if (ids == null) next.delete('clinicians');
        // An empty selection is a choice and has to survive a round trip
        // through the URL; deleting the param would read back as "everyone".
        else if (ids.length === 0) next.set('clinicians', NO_CLINICIANS);
        else next.set('clinicians', ids.join(','));
      }
      if ('order' in changes) {
        const ids = changes.order;
        if (!ids || !ids.length) next.delete('order');
        else next.set('order', ids.join(','));
      }
      // URLSearchParams escapes the separator, which turns a link somebody is
      // meant to read and paste into ?order=203%2C201. A comma is legal
      // unencoded in a query value, so it is put back.
      const query = next.toString().replace(/%2C/g, ',');
      history.push(
        `/ops/${clinicId}/${WEEKDAY_SLUGS[weekday - 1]}${
          query ? `?${query}` : ''
        }`
      );
    },
    [history, clinicId, weekday, search]
  );

  const setSelectedIds = useCallback(
    (next: number[] | null) => writeParams({ clinicians: next }),
    [writeParams]
  );
  const setOrder = useCallback(
    (next: number[] | null) => writeParams({ order: next }),
    [writeParams]
  );
  /**
   * Back to how the view opened, not to an empty query: pushing the arrival
   * search restores whatever brought the reader here — a discipline, a grade,
   * one clinician — and undoes only what they did since.
   */
  const resetView = useCallback(() => {
    const target = arrival.current?.search || '';
    history.push(
      `/ops/${clinicId}/${WEEKDAY_SLUGS[weekday - 1]}${target}`
    );
  }, [history, clinicId, weekday]);

  // Changing weekday keeps the selection and the arrangement: it is a filter on
  // this screen, not a trip to a different one.
  const goToWeekday = useCallback(
    (next: number) =>
      history.push(
        `/ops/${clinicId}/${WEEKDAY_SLUGS[next - 1]}${search || ''}`
      ),
    [history, clinicId, search]
  );

  const { loading, error, data } = useQuery(GET_CLINIC_DAY_GRID, {
    variables: { clinicId: id, weekday },
    fetchPolicy: 'network-only'
  });

  // Changing weekday is a filter on this screen, not a trip to another one.
  // Content swaps its children for a spinner whenever `data` is missing, so
  // holding the last good response keeps the grid on screen while the new day
  // arrives — otherwise the whole view blanks and repaints, which is what made
  // switching days feel like page navigation.
  const [lastLoaded, setLastLoaded] = useState<any>(null);
  useEffect(() => {
    if (data) setLastLoaded(data);
  }, [data]);
  const shown = data ?? lastLoaded;

  return (
    <Content loading={loading && !lastLoaded} error={error} data={shown}>
      {({
        clinics,
        clinicDayGrid
      }: {
        clinics: IOpsClinic[];
        clinicDayGrid: IClinicDayColumn[];
      }) => (
        <ClinicDayGrid
          clinicId={clinicId}
          clinicName={
            (clinics || []).find(clinic => clinic.id === id)?.name ||
            'This clinic'
          }
          clinicTimezone={
            (clinics || []).find(clinic => clinic.id === id)?.timezone ??
            undefined
          }
          weekday={weekday}
          columns={clinicDayGrid}
          onWeekdayChange={goToWeekday}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          disciplineFilter={disciplineFilter}
          specialtyFilter={specialtyFilter}
          canReset={changedSinceArrival}
          order={order}
          setOrder={setOrder}
          onReset={resetView}
        />
      )}
    </Content>
  );
};

export default ClinicDayPage;
